import { ConfigProvider as ChartConfigProvider } from '@ant-design/plots';
import { ProConfigProvider } from '@ant-design/pro-components';
import { QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, theme as antdTheme, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/bn';
import 'dayjs/locale/en';
import 'dayjs/locale/fa';
import 'dayjs/locale/id';
import 'dayjs/locale/ja';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import { type ReactNode, useEffect, useLayoutEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { appInfo } from '@/config/app';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { defaultLocale, getLocaleOption } from '@/locales/config';
import { applyDocumentLocale } from '@/locales/documentLocale';
import { antdLocales, getLocaleMessages } from '@/locales/messages';
import { queryClient } from '@/services/queryClient';
import { useLayoutSettingsStore } from '@/stores/layoutSettingsStore';
import { useLocaleStore } from '@/stores/localeStore';
import AntdGlobal from './AntdGlobal';
import { FormControlSizing } from './FormControlSizing';

export { queryClient } from '@/services/queryClient';

type AppProvidersProps = {
  children: ReactNode;
};

export default function AppProviders({ children }: AppProvidersProps) {
  const settings = useLayoutSettingsStore((state) => state.settings);
  const locale = useLocaleStore((state) => state.locale);
  const isDark = useResolvedTheme();
  const colorPrimary = settings.colorPrimary ?? appInfo.primaryColor;
  const localeOption = getLocaleOption(locale);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }, [isDark]);

  useEffect(() => {
    // A filter on body makes it the containing block of fixed overlays.
    // The document root is exempt and keeps the existing whole-page effect.
    const root = document.documentElement;
    root.style.filter = settings.colorWeak ? 'invert(80%)' : '';
    return () => {
      root.style.filter = '';
    };
  }, [settings.colorWeak]);

  useEffect(() => {
    applyDocumentLocale(locale);
    // 切换语言只更新文案和日期，管理端布局始终保持从左到右。
    document.documentElement.dir = 'ltr';
    dayjs.locale(localeOption.dayjsLocale);
  }, [locale, localeOption.dayjsLocale]);

  return (
    <IntlProvider
      locale={localeOption.key}
      defaultLocale={defaultLocale}
      messages={getLocaleMessages(localeOption.key)}
    >
      <ConfigProvider
        locale={antdLocales[localeOption.key]}
        direction="ltr"
        variant="filled"
        theme={{
          algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: {
            colorPrimary,
            fontFamily: 'var(--app-font-family)',
          },
        }}
      >
        {/* Pro 根据 AntD locale 使用内置组件字典，页面文案只交给 React Intl。 */}
        <FormControlSizing>
          <ProConfigProvider>
            <AntdApp>
              <AntdGlobal />
              <ChartConfigProvider
                common={{ theme: { type: isDark ? 'dark' : 'light' } }}
              >
                <QueryClientProvider client={queryClient}>
                  {children}
                </QueryClientProvider>
              </ChartConfigProvider>
            </AntdApp>
          </ProConfigProvider>
        </FormControlSizing>
      </ConfigProvider>
    </IntlProvider>
  );
}
